import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FormService, FormField } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { Button } from "@shared/ui/button";
import { useCategoryOptions, useSubcategoryOptions, useContract, useUpdatePlan, useGetPlanDetail } from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface PlanConfigurationProps {
  data?: any;
  allStepData?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const PlanConfiguration: React.FC<PlanConfigurationProps> = ({
  data,
  allStepData,
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
}) => {
  const { action, planId: paramPlanId } = useParams();
  const [hasInitialData] = useState(() => Object.keys(data || {}).length > 0);
  const queryPlanId = hasInitialData ? undefined : paramPlanId;
  const remoteUtilityId = useRemoteUtilityId();
  const { data: planDetail } = useGetPlanDetail(queryPlanId);
  const { mutateAsync: updatePlan, isPending } = useUpdatePlan();

  const { data: categoriesResult } = useCategoryOptions();
  
  const categoriesOptions = useMemo(() => {
    if (!Array.isArray(categoriesResult)) return [];
    return categoriesResult.map((c: any) => ({
      value: c.key || c.id || c.code,
      label: c.value || c.name || c.label || c.key
    }));
  }, [categoriesResult]);

  const [selectedCategories, setSelectedCategories] = useState<string[]>(data?.consumerCategories || []);
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>(data?.subCategories || []);

  const selectedCategoriesStr = selectedCategories.join(",");
  const { data: subcategoriesResult } = useSubcategoryOptions(selectedCategoriesStr);
  const subcategoriesOptions = useMemo(() => {
    if (!Array.isArray(subcategoriesResult)) return [];
    return subcategoriesResult.map((c: any) => ({
      value: c.key || c.id || c.code,
      label: c.value || c.name || c.label || c.key
    }));
  }, [subcategoriesResult]);

  const { data: contractsResult } = useContract(
    selectedCategories,
    selectedSubcategories
  );

  const contractsOptions = useMemo(() => {
    if (!Array.isArray(contractsResult)) return [];
    return contractsResult.map((c: any) => ({
      value: c.id || c.key || c.contract_id || c.code,
      label: c.contract_name || c.name || c.value || c.label
    }));
  }, [contractsResult]);

  const planConfigFields: FormField[] = useMemo(() => [
    {
      name: "consumerCategories",
      label: "Categories",
      type: "multi-select",
      required: true,
      placeholder: "Select categories...",
      options: categoriesOptions,
      maxDisplayItems: 2,
      helperText: "Choose the categories that this plan applies to.",
      classes: FIELD_CLASSES,
      className: "col-span-1",
      group: "Eligibility",
      groupColorNeeded: false,
      groupHelperText:
        "Define which categories and sub-categories this plan applies to. Pick the utility services on the next step.",
    },
    {
      name: "subCategories",
      label: "Sub categories",
      type: "multi-select",
      placeholder: "Select sub categories...",
      options: subcategoriesOptions,
      maxDisplayItems: 3,
      required:true,
      helperText:
        "Leave empty to apply to all sub categories within the selected categories.",
      classes: FIELD_CLASSES,
      groupColorNeeded: false,
      className: "col-span-1",
      group: "Eligibility",
    },
    {
      name: "contracts",
      label: "Contracts",
      type: "multi-select",
      placeholder: "Select contracts...",
      options: contractsOptions,
      helperText: "Choose which contracts are attached to this plan.",
      classes: FIELD_CLASSES,
      className: "col-span-2",
      groupColorNeeded: false,
      group: "Attached Contracts",
      groupHelperText:
        "Link one or more master contracts or service agreements that govern this plan.",
    },
  ], [categoriesOptions, subcategoriesOptions, contractsOptions]);

  const planConfigForm = FormService.createForm({
    fields: planConfigFields,
    defaultValues: data || {},
    mode: "onChange",
  });

  useEffect(() => {
    const subscription = planConfigForm.watch((value, { name }) => {
      if (name === "consumerCategories" || !name) {
        setSelectedCategories(value.consumerCategories || []);
      }
      if (name === "subCategories" || !name) {
        setSelectedSubcategories(value.subCategories || []);
      }
    });
    return () => subscription.unsubscribe();
  }, [planConfigForm]);

  const isLocalStorageRestored = React.useRef(false);

  useEffect(() => {
    if (hasInitialData && data && !isLocalStorageRestored.current) {
      FormService.populateForm(planConfigForm, data, planConfigFields);
      isLocalStorageRestored.current = true;
    }
  }, [hasInitialData, data, planConfigForm, planConfigFields]);

  useEffect(() => {
    if (hasInitialData) return;
    if (planDetail?.result && paramPlanId && paramPlanId !== "create") {
      const result = planDetail.result;
      const eligibility = result.overview?.eligibility;
      
      const valuesToSet = {
        consumerCategories: eligibility?.categories?.map((category: any) => category.code || category.id) || [],
        subCategories: eligibility?.subCategories?.map((subCategory: any) => subCategory.code || subCategory.id) || [],
        contracts: result.contractIds || [],
      };

      FormService.populateForm(planConfigForm, valuesToSet, planConfigFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planDetail, paramPlanId, planConfigForm]);


  const onDataChangeRef = React.useRef(onDataChange);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  useEffect(() => {
    if (!onDataChangeRef.current) return;
    // Emit current values immediately on mount so Review has data even if user never touches this step
    onDataChangeRef.current(planConfigForm.getValues());
    const subscription = planConfigForm.watch((value) => {
      onDataChangeRef.current?.(value);
    });
    return () => subscription.unsubscribe();
  }, [planConfigForm]);

  const handleNextSubmit = async () => {
    const isValid = await FormService.validateForm(planConfigForm);

    if (isValid) {
      // Step 0 is BasicInfo where planId is created
      const planId = paramPlanId || allStepData?.[0]?.planId || data?.planId;
      if (planId) {
        try {
          const formData = planConfigForm.getValues();
          const payload = {
            current_step: 2,
            categories: formData.consumerCategories || [],
            sub_categories: formData.subCategories || [],
            contract_ids: formData.contracts ? formData.contracts.map((c: any) => Number(c)) : [],
            remote_utility_id: remoteUtilityId
          };
          if (action !== 'edit') {
            await updatePlan({ planId, payload });
          }
          onNext?.();
        } catch (error) {
          console.error("Failed to update plan configuration:", error);
        }
      } else {
        console.warn("No planId found from previous step!");
        onNext?.();
      }
    }
  };

  return (
    <div className="space-y-6">
      <DynamicForm
        fields={planConfigFields}
        form={planConfigForm}
        gridColumns={2}
        config={{
          showSubmitButton: false,
          showResetButton: false,
        }}
      />

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-6 border-t pt-6">
        <div>
          {!isFirstStep && onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
        </div>
        <Button type="button" onClick={handleNextSubmit} disabled={isPending}>
          {isPending ? "Saving..." : (isLastStep ? "Finish" : "Next")}
        </Button>
      </div>
    </div>
  );
};

export default PlanConfiguration;
